/**
 * PricingSection.tsx
 * 料金関連: コンパクトなサマリー表示。クリックで編集モーダル（PricingEditModal）を開く。
 * ※ 会計の正は Square 請求書（基本情報セクション）。ここは予約データ側の料金。
 */
import React, { useState } from 'react';
import { SectionProps } from '../types';
import SectionHeader from '../shared/SectionHeader';
import PricingEditModal from '../modals/PricingEditModal';
import { useFieldMark, markBoxStyle, FieldMarkChip } from '../conflict/fieldState';
import { getCdwBundle, getNocBundle, getLocationBundle } from '../../../lib/cdwBundle';

const PRICING_FIELDS = [
  'base_price', 'option_price', 'option', 'noc', 'noc_price', 'cdw', 'cdw_price',
  'coupon', 'point', 'nights', 'days', 'sum_price',
] as const;

interface PricingSectionProps extends SectionProps {
  onRecalcClick?: () => void;
}

function yen(v: number | null | undefined): string {
  if (v == null) return '-';
  return `¥${Number(v).toLocaleString()}`;
}

export default function PricingSection({
  data,
  onChange,
  disabled,
  onRecalcClick,
}: PricingSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const pricingMark = useFieldMark(PRICING_FIELDS);
  // 「基本料金に含める」プランの印（LP が json_data に入れる）。base_price に含み、各料金欄は 0。
  //  CDW: 繁体字・韓国語（2026-10-05） / NOC: 宮古の繁体字・韓国語（2026-10-21）
  //  送迎費: 宮古の下地島空港・全言語（2026-10-21）
  const cdwBundle = getCdwBundle(data.json_data);
  const nocBundle = getNocBundle(data.json_data);
  const locationBundle = getLocationBundle(data.json_data);

  const cellLabel: React.CSSProperties = {
    fontSize: '0.72rem',
    color: '#64748b',
    whiteSpace: 'nowrap',
  };
  const cellValue: React.CSSProperties = {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#0f172a',
    whiteSpace: 'nowrap',
  };

  const cells: Array<{ label: string; value: string; muted?: boolean }> = [
    { label: '基本料金', value: yen(data.base_price) },
    { label: 'オプション', value: yen(data.option_price) },
    {
      label: 'NOC',
      value: nocBundle.bundled
        ? `基本料金に込み${nocBundle.amount != null ? `（${yen(nocBundle.amount)}）` : ''}`
        : (data.noc ? yen(data.noc_price) : '—'),
      muted: !data.noc && !nocBundle.bundled,
    },
    {
      label: 'CDW',
      value: cdwBundle.bundled
        ? `基本料金に込み${cdwBundle.amount != null ? `（${yen(cdwBundle.amount)}）` : ''}`
        : (data.cdw ? yen(data.cdw_price) : '—'),
      muted: !data.cdw && !cdwBundle.bundled,
    },
    { label: 'クーポン', value: data.coupon || '—', muted: !data.coupon },
    { label: 'ポイント', value: `${data.point ?? 0} pt`, muted: !data.point },
    { label: '泊数', value: data.nights != null && data.nights !== 0 ? `${data.nights} 泊` : '—', muted: data.nights == null || data.nights === 0 },
    { label: '日数', value: data.days != null && data.days !== 0 ? `${data.days} 日` : '—', muted: data.days == null || data.days === 0 },
  ];

  return (
    <>
      <SectionHeader title="料金" />
      {cdwBundle.bundled && (
        <div
          data-testid="cdw-bundled-chip"
          title={`繁体字・韓国語の LP 予約。基本料金に ${nocBundle.bundled ? 'CDW・NOC' : 'CDW'} を含めて表示・請求している（${nocBundle.bundled ? 'CDW・NOC' : 'CDW'} の料金欄は 0 円）。日数や車種を変えたら、基本料金に含める補償も合わせて直す`}
          style={{
            margin: '4px 4px 0', display: 'inline-block', padding: '2px 8px', borderRadius: 10,
            fontSize: '0.75rem', fontWeight: 700, color: '#065f46', background: '#d1fae5', border: '1px solid #6ee7b7',
          }}
        >
          {nocBundle.bundled ? 'CDW・NOC込みプラン' : 'CDW込みプラン'}
          {cdwBundle.amount != null ? `（基本料金に CDW ${yen(cdwBundle.amount)}${nocBundle.amount != null ? `・NOC ${yen(nocBundle.amount)}` : ''} を含む）` : ''}
        </div>
      )}
      {locationBundle.bundled && (
        <div
          data-testid="location-bundled-chip"
          title="宮古・下地島空港発着。送迎費を基本料金に含めて表示・請求している（「送迎場所 追加料金」の別行は出さない）。日程や送迎場所を変えたら基本料金も合わせて直す"
          style={{
            margin: '4px 4px 0', display: 'inline-block', padding: '2px 8px', borderRadius: 10,
            fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', background: '#dbeafe', border: '1px solid #93c5fd',
          }}
        >
          送迎費込み{locationBundle.amount != null ? `（基本料金に 下地島送迎 ${yen(locationBundle.amount)} を含む）` : ''}
        </div>
      )}
      {pricingMark && (
        <div style={{ margin: '4px 4px 0', fontSize: '0.75rem' }}>
          <FieldMarkChip mark={pricingMark} />
        </div>
      )}
      <div
        data-field={PRICING_FIELDS.join(' ')}
        className={pricingMark ? `gc-field--${pricingMark}` : undefined}
        role={disabled ? undefined : 'button'}
        tabIndex={disabled ? undefined : 0}
        onClick={() => !disabled && setModalOpen(true)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setModalOpen(true);
          }
        }}
        title={disabled ? undefined : 'クリックで料金を編集'}
        style={{
          margin: '4px 4px 8px',
          padding: '10px 12px',
          background: '#fff',
          border: '1px solid #cbd5e1',
          borderRadius: 6,
          cursor: disabled ? 'default' : 'pointer',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          ...markBoxStyle(pricingMark),
        }}
      >
        {/* 2列グリッドのサマリー */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 16,
            rowGap: 4,
          }}
        >
          {cells.map((c) => (
            <div
              key={c.label}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}
            >
              <span style={cellLabel}>{c.label}</span>
              <span style={{ ...cellValue, ...(c.muted ? { color: '#94a3b8', fontWeight: 400 } : {}) }}>
                {c.value}
              </span>
            </div>
          ))}
        </div>

        {/* オプション内容 */}
        {data.option && (
          <div
            style={{
              marginTop: 6,
              fontSize: '0.75rem',
              color: '#475569',
              lineHeight: 1.5,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {data.option}
          </div>
        )}

        {/* 合計 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginTop: 8,
            paddingTop: 6,
            borderTop: '1px solid #cbd5e1',
          }}
        >
          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155' }}>合計金額</span>
          <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
            {yen(data.sum_price)}
          </span>
        </div>

        {!disabled && (
          <div style={{ marginTop: 4, fontSize: '0.68rem', color: '#94a3b8', textAlign: 'right' }}>
            クリックで編集
          </div>
        )}
      </div>

      <PricingEditModal
        open={modalOpen}
        data={data}
        onChange={onChange}
        disabled={disabled}
        onClose={() => setModalOpen(false)}
        onRecalcClick={onRecalcClick}
      />
    </>
  );
}
